import { EmpleadoCrud } from 'src/app/pages/empleados/models/empleado';
import { DocumentoEnvioSimple } from '../models/documento-envio-simple';
import {
  DatosEnvioRltProcesamiento,
  EmpleadoProcesamientoEnvioSimple,
  EnvioSimpleProcesamientoRequest
} from '../models/envio-simple-procesamiento-api.model';
import {
  EnvioSimpleConfirmacionResultado,
  EstadoEnvioFirmanteUi,
  EstadoGlobalEnvioSimple,
  FirmanteEnvioSimpleResultado
} from '../models/envio-simple-confirmacion.model';
import { FirmanteEnvioSimple } from '../models/firmante-envio-simple';
import { EnvioSimpleFlowService } from './envio-simple-flow.service';
import { ResponseSubidaFicheros } from 'src/app/pages/subidaPrincipal/models/response-subida-ficheros';
import { EnvioSimpleProcesamientoApiResponse } from '../models/envio-simple-procesamiento-api.model';
import { ProcesaEnvioSimpleApiResponse, ProcesaEnvioSimpleErrorApi, ProcesaEnvioSimpleFirmanteResultadoApi, ProcesaEnvioSimpleResultadoApi } from '../models/envio-simple-procesa-envio-simple.model';

const DATOS_ENVIO_RLT_VACIO: DatosEnvioRltProcesamiento = {
  Introduccion_Rlt: null,
  Fecha_Envio_Rlt: null,
  Hora_Envio_Rlt: null,
  Contenido_rlt: null,
  Periodo_ini_rlt: null,
  Periodo_fin_rlt: null,
  Lista_centros_rlt: null,
  Nombre_rlt: null,
  Firma_rlt: null
};

export function normalizarNombreFicheroSubida(fileName: string): string {
  return fileName.replace(/[^a-zA-Z0-9_\-\.]+/g, '');
}

export function resolverArchivoDocumento(documento: DocumentoEnvioSimple): File {
  if (documento.archivo) {
    return documento.archivo;
  }
  if (documento.pdfBlob) {
    const nombreBase = documento.nombre.toLowerCase().endsWith('.pdf')
      ? documento.nombre
      : `${documento.nombre}.pdf`;
    return new File([documento.pdfBlob], normalizarNombreFicheroSubida(nombreBase), {
      type: 'application/pdf'
    });
  }
  throw new Error(`El documento "${documento.nombre}" no tiene fichero asociado.`);
}

export function construirRequestProcesamiento(
  estado: ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']>,
  idSubida: number,
  idTipoEnvio: number,
  isEnvioTest: boolean,
  wait = true
): EnvioSimpleProcesamientoRequest {
  return {
    idTipoEnvio,
    idSubida,
    empleados: estado.firmantesEnvio.map(firmante =>
      mapFirmanteEnvioAEmpleadoApi(
        firmante,
        estado.documentos.map(item => item.documento)
      )
    ),
    isEnvioTest,
    Wait: wait
  };
}

export function mapFirmanteEnvioAEmpleadoApi(
  firmante: FirmanteEnvioSimple,
  documentos: DocumentoEnvioSimple[]
): EmpleadoProcesamientoEnvioSimple {
  const empleado = construirEmpleadoDesdeFirmante(firmante);
  const firmantesDocumento = recogerCodigosFirmanteEnDocumentos(firmante.codigo, documentos);

  return {
    Identificativos: {
      IDA3Trabajador: valorNuloSiVacio(empleado.codigo),
      CodigoPerfilSeguridad: null,
      LugarNacimiento: empleado.lugarNacimiento ?? '',
      Nacionalidad: empleado.nacionalidad ?? '',
      FechaNacimiento: valorFechaApi(empleado.fechaNacimiento),
      EstadoCivil: empleado.estadoCivil ?? '',
      Sexo: empleado.sexo ?? '',
      SegundoApellido: empleado.apellido2 ?? firmante.apellido2 ?? '',
      DescripcionPerfilSeguridad: null,
      PrimerApellido: empleado.apellido1 ?? firmante.apellido1 ?? '',
      DescripcionTipoDocumento: empleado.tipoDocumento ?? '',
      CodigoTipoDocumento: null,
      NombreTrabajador: empleado.nombre ?? firmante.nombre ?? '',
      NIFTrabajador: empleado.nif ?? '',
      CentroTrabajador: empleado.codigoCentro ?? '',
      NombreCentro: empleado.nombreCentro ?? '',
      EmpresaTrabajador: empleado.codigoEmpresa ?? '',
      CodigoTrabajador: empleado.codigoExterno ?? empleado.codigo ?? '',
      TipoPersona: empleado.tipoPersona ?? ''
    },
    Domicilio: {
      Sigla: empleado.sigla ?? '',
      Email1: firmante.email ?? empleado.mailPersonal ?? '',
      Fax: null,
      Extension2: '',
      Telefono2: '',
      Extension1: empleado.extensionTelefonoPersonal ?? '',
      Telefono1: firmante.telefono ?? empleado.telefonoPersonal ?? '',
      Email2: empleado.mailProfesional ?? '',
      CodigoPostal: empleado.codigoPostal ?? '',
      Municipio: empleado.municipio ?? '',
      Puerta: empleado.puerta ?? '',
      Piso: empleado.piso ?? '',
      Escalera: empleado.escalera ?? '',
      Numero: empleado.numero ?? '',
      ViaPublica: null,
      Provincia: empleado.provincia ?? ''
    },
    DatosEmpresa: {
      IDA3Empresa: null,
      EmpresaTrabajador: empleado.codigoEmpresa ?? null,
      CodigoEmpresa: empleado.codigoEmpresa ?? '',
      NombreEmpresa: empleado.nombreEmpresa ?? '',
      NIFEmpresa: empleado.empresaCif ?? '',
      CodigoTipoDocumento: null,
      DescripcionTipoDocumento: null
    },
    Laborales: {
      FechaAlta: valorFechaApi(empleado.fechaAlta),
      FechaBaja: null,
      IDA3PuestoTrabajo: null,
      CodigoPuestoTrabajo: null,
      DescripcionPuestoTrabajo: empleado.puestoTrabajo ?? '',
      IDA3Categoria: null,
      CodigoCategoria: null,
      DescripcionCategoria: empleado.categoria ?? '',
      IDA3Convenio: null,
      CodigoConvenio: null,
      DescripcionConvenio: empleado.convenio ?? ''
    },
    Imputacion: {
      IDA3NodoImputacion: null,
      CodigoNodoImputacion: null,
      DescripcionNodoImputacion: null,
      CodigoNodoNivel1: null,
      CodigoNodoNivel2: null,
      CodigoNodoNivel3: null,
      CodigoNodoNivel4: null,
      CodigoNodoNivel5: null,
      Porcentaje: null
    },
    Periodos: [],
    Firmantes: firmantesDocumento,
    OrdenFirmante: firmante.orden,
    DatosEnvioRlt: { ...DATOS_ENVIO_RLT_VACIO }
  };
}

export function mapRespuestaApiAConfirmacion(
  response: EnvioSimpleProcesamientoApiResponse,
  firmantesEnvio: FirmanteEnvioSimple[]
): EnvioSimpleConfirmacionResultado {
  if (!response?.Ok) {
    const mensaje = response?.ExceptionMessage ?? response?.mensajeError ?? 'El envío no se ha completado.';
    return {
      estadoGlobal: 'Error',
      mensajePrincipal: mensaje,
      firmantes: firmantesEnvio.map(firmante => mapFirmanteBaseResultado(firmante, 'Error', mensaje))
    };
  }

  const log = response.Data?.log;
  const firmantes = firmantesEnvio.map(firmante =>
    mapFirmanteResultadoDesdeLog(firmante, log)
  );
  const hayErrorDocumento = (log?.ficheros ?? []).some(fichero => normalizarEstado(fichero.estado) === 'error');
  const hayErrorFirmante = firmantes.some(firmante => firmante.estadoEnvio === 'Error');
  const hayEnProceso = firmantes.some(firmante => firmante.estadoEnvio === 'En proceso');
  const estadoGlobal: EstadoGlobalEnvioSimple =
    hayErrorDocumento || hayErrorFirmante ? 'Error' : hayEnProceso ? 'Error' : 'Exito';

  const mensajePrincipal =
    estadoGlobal === 'Exito'
      ? response.Data?.mensaje ?? 'El envío se ha procesado correctamente.'
      : response.Data?.mensaje ?? 'El envío no se ha completado. Revisa los registros con error.';

  return { estadoGlobal, mensajePrincipal, firmantes };
}

/** Adapta la respuesta de `POST api/Envios/ProcesaEnvioSimple` a la pantalla de confirmacion. */
export function mapProcesaEnvioSimpleRespuestaAConfirmacion(
  response: ProcesaEnvioSimpleApiResponse | ProcesaEnvioSimpleResultadoApi | null | undefined,
  firmantesEnvio: FirmanteEnvioSimple[]
): EnvioSimpleConfirmacionResultado {
  const normalizada = normalizarRespuestaProcesaEnvioSimple(response);

  if (!normalizada.ok || !normalizada.data) {
    const mensaje =
      normalizada.exceptionMessage ??
      normalizada.mensajeError ??
      'El envío no se ha completado.';
    return {
      estadoGlobal: 'Error',
      mensajePrincipal: mensaje,
      firmantes: firmantesEnvio.map(firmante => mapFirmanteBaseResultado(firmante, 'Error', mensaje))
    };
  }

  const data = normalizada.data;
  const firmantesApi = data.firmantes ?? data.Firmantes;

  if (firmantesApi?.length) {
    const firmantes = mapFirmantesDesdeResultadoApi(firmantesApi, firmantesEnvio);
    const estadoGlobal = resolverEstadoGlobalDesdeResultado(data, firmantes);
    const mensajePrincipal = resolverMensajePrincipalDesdeResultado(
      data,
      estadoGlobal,
      normalizada.exceptionMessage
    );

    return { estadoGlobal, mensajePrincipal, firmantes };
  }

  return mapRespuestaApiAConfirmacion(
    {
      Ok: normalizada.ok,
      Data: {
        log: data.log ?? data.Log,
        finalizado: data.finalizado ?? data.Finalizado,
        mensaje: data.mensaje ?? data.Mensaje ?? data.mensajePrincipal ?? data.MensajePrincipal
      },
      ExceptionMessage: normalizada.exceptionMessage,
      mensajeError: normalizada.mensajeError
    },
    firmantesEnvio
  );
}

export function mapEstadoBackendAUi(estado: string | null | undefined): EstadoEnvioFirmanteUi {
  const normalizado = normalizarEstado(estado);
  if (normalizado === 'finalizado' || normalizado === 'exito' || normalizado === 'ok') {
    return 'Finalizado';
  }
  if (normalizado === 'error') {
    return 'Error';
  }
  return 'En proceso';
}

function normalizarRespuestaProcesaEnvioSimple(
  response: ProcesaEnvioSimpleApiResponse | ProcesaEnvioSimpleResultadoApi | null | undefined
): {
  ok: boolean;
  data: ProcesaEnvioSimpleResultadoApi | null;
  exceptionMessage?: string;
  mensajeError?: string;
} {
  if (!response) {
    return { ok: false, data: null };
  }

  const comoEnvelope = response as ProcesaEnvioSimpleApiResponse;
  const tieneEnvelope =
    Object.prototype.hasOwnProperty.call(response, 'Ok') ||
    Object.prototype.hasOwnProperty.call(response, 'ok') ||
    Object.prototype.hasOwnProperty.call(response, 'Data') ||
    Object.prototype.hasOwnProperty.call(response, 'data');

  if (tieneEnvelope) {
    const ok = (comoEnvelope as { Ok?: boolean; ok?: boolean }).Ok
      ?? (comoEnvelope as { ok?: boolean }).ok
      ?? false;
    return {
      ok: !!ok,
      data: (comoEnvelope.Data ?? (comoEnvelope as { data?: ProcesaEnvioSimpleResultadoApi }).data) ?? null,
      exceptionMessage: comoEnvelope.ExceptionMessage,
      mensajeError: comoEnvelope.mensajeError
    };
  }

  const comoResultado = response as ProcesaEnvioSimpleResultadoApi;
  const pareceResultado =
    comoResultado.EstadoGlobal != null ||
    comoResultado.estadoGlobal != null ||
    comoResultado.Firmantes != null ||
    comoResultado.firmantes != null ||
    comoResultado.Documentos != null ||
    comoResultado.documentos != null;

  if (pareceResultado) {
    return { ok: true, data: comoResultado };
  }

  return { ok: false, data: null };
}

function mapFirmantesDesdeResultadoApi(
  firmantesApi: ProcesaEnvioSimpleFirmanteResultadoApi[],
  firmantesEnvio: FirmanteEnvioSimple[]
): FirmanteEnvioSimpleResultado[] {
  return firmantesApi.map((item, index) => {
    const codigoApi = String(item.Codigo ?? item.codigo ?? '').trim();
    const local =
      firmantesEnvio.find(firmante => String(firmante.codigo) === codigoApi) ??
      firmantesEnvio[index];
    const estadoRaw =
      item.EstadoEnvio ??
      item.estadoEnvio ??
      item.Estado ??
      item.estado;
    const mensaje =
      item.MensajeEstado ??
      item.mensajeEstado ??
      item.Mensaje ??
      item.mensaje;

    if (local) {
      return mapFirmanteBaseResultado(local, mapEstadoBackendAUi(estadoRaw), mensaje);
    }

    return {
      codigo: codigoApi || `api-${index}`,
      orden: item.Orden ?? item.orden ?? index + 1,
      tipoFirmante: 'Firmante',
      esValidador: false,
      nombre: item.Nombre ?? item.nombre ?? '',
      apellido1: item.Apellido1 ?? item.apellido1 ?? '',
      apellido2: item.Apellido2 ?? item.apellido2 ?? '',
      email: item.Email ?? item.email ?? '',
      telefono: item.Telefono ?? item.telefono ?? '',
      estadoEnvio: mapEstadoBackendAUi(estadoRaw),
      mensajeEstado: mapEstadoBackendAUi(estadoRaw) === 'Error' ? mensaje : undefined
    };
  });
}

function resolverEstadoGlobalDesdeResultado(
  data: ProcesaEnvioSimpleResultadoApi,
  firmantes: FirmanteEnvioSimpleResultado[]
): EstadoGlobalEnvioSimple {
  const estadoGlobalRaw = data.EstadoGlobal ?? data.estadoGlobal;
  if (estadoGlobalRaw != null && String(estadoGlobalRaw).trim().length > 0) {
    return mapEstadoGlobalBackendAUi(estadoGlobalRaw);
  }

  const documentos = data.Documentos ?? data.documentos ?? [];
  const hayErrorDocumento = documentos.some(
    documento => normalizarEstado(documento.Estado ?? documento.estado) === 'error'
  );
  const hayErrorFirmante = firmantes.some(firmante => firmante.estadoEnvio === 'Error');
  const hayEnProceso = firmantes.some(firmante => firmante.estadoEnvio === 'En proceso');
  const hayErrores = extraerMensajesErrores(data.Errores ?? data.errores).length > 0;

  return hayErrorDocumento || hayErrorFirmante || hayEnProceso || hayErrores ? 'Error' : 'Exito';
}

function mapEstadoGlobalBackendAUi(estado: string | null | undefined): EstadoGlobalEnvioSimple {
  const normalizado = normalizarEstado(estado);
  if (normalizado === 'finalizado' || normalizado === 'exito' || normalizado === 'ok') {
    return 'Exito';
  }
  return 'Error';
}

function resolverMensajePrincipalDesdeResultado(
  data: ProcesaEnvioSimpleResultadoApi,
  estadoGlobal: EstadoGlobalEnvioSimple,
  exceptionMessage?: string
): string {
  const mensajeDirecto =
    data.MensajePrincipal ??
    data.mensajePrincipal ??
    data.Mensaje ??
    data.mensaje;
  if (typeof mensajeDirecto === 'string' && mensajeDirecto.trim().length > 0) {
    return mensajeDirecto.trim();
  }

  const errores = extraerMensajesErrores(data.Errores ?? data.errores);
  if (errores.length > 0) {
    return errores.join(' ');
  }

  if (exceptionMessage && exceptionMessage.trim().length > 0) {
    return exceptionMessage.trim();
  }

  if (estadoGlobal === 'Exito') {
    const estadoGlobalRaw = data.EstadoGlobal ?? data.estadoGlobal;
    if (estadoGlobalRaw && String(estadoGlobalRaw).trim().length > 0) {
      return `Envío ${String(estadoGlobalRaw).trim()}.`;
    }
    return 'El envío se ha procesado correctamente.';
  }

  return 'El envío no se ha completado. Revisa los registros con error.';
}

function extraerMensajesErrores(
  errores: ProcesaEnvioSimpleErrorApi[] | null | undefined
): string[] {
  if (!Array.isArray(errores)) {
    return [];
  }

  return errores
    .map(error => {
      if (typeof error === 'string') {
        return error.trim();
      }
      return (
        error?.Mensaje ??
        error?.mensaje ??
        error?.Message ??
        error?.message ??
        ''
      ).trim();
    })
    .filter(mensaje => mensaje.length > 0);
}

function construirEmpleadoDesdeFirmante(firmante: FirmanteEnvioSimple): EmpleadoCrud {
  if (firmante.datosEmpleado) {
    return { ...(firmante.datosEmpleado as EmpleadoCrud) };
  }
  return {
    codigo: firmante.codigoEmpleado ?? '',
    tipoDocumento: '',
    nif: '',
    fechaNacimiento: '',
    nombre: firmante.nombre,
    apellido1: firmante.apellido1,
    apellido2: firmante.apellido2,
    nacionalidad: '',
    lugarNacimiento: '',
    estadoCivil: '',
    sexo: '',
    tipoPersona: '',
    sigla: '',
    provincia: '',
    municipio: '',
    codigoPostal: '',
    numero: '',
    escalera: '',
    piso: '',
    puerta: '',
    mailPersonal: firmante.email,
    extensionTelefonoPersonal: '',
    telefonoPersonal: firmante.telefono,
    mailProfesional: '',
    extensionTelefonoProfesional: '',
    telefonoProfesional: '',
    fechaAlta: '',
    puestoTrabajo: '',
    categoria: '',
    convenio: '',
    codigoEmpresa: '',
    codigoCentro: '',
    empresaCif: '',
    nombreEmpresa: '',
    nombreCentro: '',
    codigoExterno: ''
  };
}

function recogerCodigosFirmanteEnDocumentos(
  codigoFirmante: string,
  documentos: DocumentoEnvioSimple[]
): string[] {
  const codigos = new Set<string>();
  documentos.forEach(documento => {
    const firmantesNif = documento.tipoDocumento?.firmantesNif ?? documento.configuracion?.firmantesNif;
    if (!Array.isArray(firmantesNif)) {
      return;
    }
    const asignado = firmantesNif.some((item: { codigo?: string }) => item?.codigo === codigoFirmante);
    if (asignado) {
      codigos.add(codigoFirmante);
    }
  });
  return [...codigos];
}

function mapFirmanteResultadoDesdeLog(
  firmante: FirmanteEnvioSimple,
  log: ResponseSubidaFicheros | undefined
): FirmanteEnvioSimpleResultado {
  if (!log) {
    return mapFirmanteBaseResultado(firmante, 'Finalizado');
  }

  const email = (firmante.email ?? '').trim().toLowerCase();
  const telefono = (firmante.telefono ?? '').trim();
  const logFirmante = (log.firmantes ?? []).find(item => {
    const mailLog = (item.mail ?? '').trim().toLowerCase();
    return mailLog.length > 0 && mailLog === email;
  });

  if (logFirmante) {
    const fichero = (log.ficheros ?? []).find(item => item.id === logFirmante.idFichero);
    const mensaje =
      (log.logs ?? []).find(item => item.id === logFirmante.idFichero)?.log ??
      (log.logs ?? []).find(item => item.idSubida === fichero?.idSubida)?.log ??
      fichero?.mensaje;
    return mapFirmanteBaseResultado(
      firmante,
      mapEstadoBackendAUi(logFirmante.estado ?? fichero?.estado),
      mensaje
    );
  }

  const ficheroPorContacto = (log.ficheros ?? []).find(item => {
    const mailFichero = (item.emailEmpleado ?? '').trim().toLowerCase();
    return (email.length > 0 && mailFichero === email) || (telefono.length > 0 && item.nombreEmpleado === firmante.nombre);
  });

  if (ficheroPorContacto) {
    const mensaje = (log.logs ?? []).find(item => item.idSubida === ficheroPorContacto.idSubida)?.log;
    return mapFirmanteBaseResultado(
      firmante,
      mapEstadoBackendAUi(ficheroPorContacto.estado),
      ficheroPorContacto.mensaje ?? mensaje
    );
  }

  const hayErrorGlobal = (log.ficheros ?? []).some(item => normalizarEstado(item.estado) === 'error');
  return mapFirmanteBaseResultado(
    firmante,
    hayErrorGlobal ? 'Error' : mapEstadoBackendAUi(log.subida?.estado)
  );
}

function mapFirmanteBaseResultado(
  firmante: FirmanteEnvioSimple,
  estadoEnvio: EstadoEnvioFirmanteUi,
  mensajeEstado?: string
): FirmanteEnvioSimpleResultado {
  return {
    codigo: firmante.codigo,
    orden: firmante.orden,
    tipoFirmante: firmante.esValidador ? 'Validador' : firmante.desc,
    esValidador: firmante.esValidador,
    nombre: firmante.nombre,
    apellido1: firmante.apellido1,
    apellido2: firmante.apellido2,
    email: firmante.email,
    telefono: firmante.telefono,
    estadoEnvio,
    mensajeEstado: estadoEnvio === 'Error' ? mensajeEstado : undefined
  };
}

function valorNuloSiVacio(valor: string | null | undefined): string | null {
  const limpio = (valor ?? '').trim();
  return limpio.length > 0 ? limpio : null;
}

function valorFechaApi(valor: string | null | undefined): string | null {
  const limpio = (valor ?? '').trim();
  if (!limpio) {
    return null;
  }
  const fecha = new Date(limpio);
  return Number.isNaN(fecha.getTime()) ? null : fecha.toISOString();
}

function normalizarEstado(estado: string | null | undefined): string {
  return (estado ?? '').trim().toLowerCase();
}
